
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { DateTime } from 'luxon';
import { getTimeArray } from '@/utils/timeFunctions';
import { cleanAvailabilities, initTempAvailability, resetAvailabilities, resetAvailability, setAvailability, setTempWeekDays } from '@/utils/availability';
import { WEEKDAYS } from '@/utils/constants';
import Toast from '@/utils/toast';
import SmallModalWrapper from '@/components/shared/SmallModalWrapper.vue';
import CopyAvailability from '@/components/menu/form/CopyAvailability.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';

const namespace: string = 'menus';

@Component<Availability>({
	components: {
		SelectComponent,
		SmallModalWrapper,
		CopyAvailability,
		TextButton,
		ToggleSwitch
	}
})
export default class Availability extends Vue {
	@Action('updateMenuAvailability', { namespace }) private updateMenuAvailability!: (payload: any) => Promise<void>;
	@Prop({ type: Boolean, default: false }) private saveSection!: boolean;
	@Prop({ type: Boolean, default: false }) private showSaveBtn!: boolean;
	@Prop({ type: Object, default: () => {} }) private menu!: Menu;
	@Prop({ type: String, default: '' }) private title!: string;
	@Prop({ type: Boolean, default: false }) private inEditorModal!: boolean;
	@Prop({ type: Boolean, default: false }) private isK12Location!: boolean;
	@Prop({ type: Boolean, default: false }) private isCreating!: boolean;
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;

	private dailyHours: boolean = false;
	private loading: boolean = false;
	private showCopyHoursDialog: boolean = false;
	private mutableMenu: Menu = this.menu;
	private timeAvailabilityArray: string[] = getTimeArray(15, 'minutes');
	private tempWeekDays: object[] = this.initWeekDays();

	/**
	 * Set the default availability if there is none.
	 * Otherwise check if the availability is all week.
	 * If it isn't, we need to check which day is set
	 * and their respective start_time/end_time.
	 *
	 * @return {void}
	 */
	private created(): void {
		if (this.mutableMenu.availability && this.mutableMenu.availability.length) {
			this.mutableMenu.availability = setAvailability(this.mutableMenu.availability);
			let availabilityConfig: any = setTempWeekDays(this.mutableMenu.availability, this.tempWeekDays);

			this.tempWeekDays = availabilityConfig.weekDays;
			if (!availabilityConfig.isAllWeek) {
				this.dailyHours = true;
			}
		}
		else {
			this.mutableMenu.availability = initTempAvailability();
		}

		// If it's a K12 location set menu availability to 24hrs all week
		if (this.isK12Location && this.isCreating) {
			this.dailyHours = false;
			this.mutableMenu.availability.forEach((availability: MenuAvailability) => {
				availability.start_time = '00:00';
				availability.end_time = '00:00';
			});
		}
	}

	private initWeekDays(): object[] {
		return WEEKDAYS.map((day) => ({
			name: day.name,
			number: day.number,
			checked: true,
			start_time: null,
			end_time: null
		}));
	}

	private updateType(): void {
		this.mutableMenu.availability = resetAvailabilities(this.mutableMenu.availability!);
		this.tempWeekDays = this.initWeekDays();
	}

	private updateSingleDay(value: boolean, day: number): void {
		if (!value) {
			this.mutableMenu.availability![day - 1] = resetAvailability(this.mutableMenu.availability![day - 1]);
		}
	}

	/**
	 * Update the start_time/end_time of an availability.
	 * If it's for dailyHours we simply loop through
	 *
	 * @param {string} value
	 * @param {boolean} isAllWeek
	 * @param {boolean} isStartTime
	 * @param {number} day
	 */
	private updateTime(value: string, isAllWeek: boolean, isStartTime: boolean, day: number): void {
		if (isAllWeek) {
			this.mutableMenu.availability!.forEach((availability: MenuAvailability) => {
				availability.menu_id = this.mutableMenu.id;
				availability.start_time = isStartTime ? DateTime.fromFormat(value, 'HH:mm').toFormat('HH:mm') : availability.start_time;
				availability.end_time = isStartTime ? availability.end_time : DateTime.fromFormat(value, 'HH:mm').toFormat('HH:mm');
			});
		}
		else {
			this.mutableMenu.availability![day - 1].menu_id = this.mutableMenu.id;
			this.mutableMenu.availability![day - 1].start_time = isStartTime ? DateTime.fromFormat(value, 'HH:mm').toFormat('HH:mm') : this.mutableMenu.availability![day - 1].start_time;
			this.mutableMenu.availability![day - 1].end_time = isStartTime ? this.mutableMenu.availability![day - 1].end_time : DateTime.fromFormat(value, 'HH:mm').toFormat('HH:mm');
		}
		this.$emit('time-updated');
	}

	/**
	 * Save the updated menu's availability (for Availabilities.vue)
	 *
	 * @return {Promise<void>}
	 */
	private async save(copiedAvailability: boolean = false): Promise<void> {
		this.loading = true;
		await this.updateMenuAvailability({ menu: this.menu, availability: cleanAvailabilities(this.mutableMenu.availability!) })
			.then(() => {
				const message = copiedAvailability ? this.$t('menu.editor.availability.success_message_save_and_copy', { menuName: this.menu.name }) : this.$t('menu.editor.availability.success_message_save', { menuName: this.menu.name });
				Toast.success(message);
				if (copiedAvailability) {
					this.$emit('page-load');
				}
			})
			.catch((errorMessage) => {
				Toast.error(this.$t('menu.editor.availability.error_saving', { menuName: this.menu.name, errorMessage }));
			});
		this.loading = false;
	}

	/**
	 * Send the success/error response to parent for the toast to show
	 * Close the dialog if the request was successful
	 *
	 * @return {Promise<void>}
	 */
	private async copyConfirm(): Promise<void> {
		await this.save(true);
		this.showCopyHoursDialog = false;
	}
}
